import { useAuth0 } from 'auth0/reactAuth0Spa';
import { checkLoginForError, formatBearerToken } from 'Common.functions';
import { DepartmentContext } from 'DepartmentWrapper';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardBody, CardHeader, FormGroup, Input, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import { deleteData, fetchDataAgnostic, postDataAgnostic, putData } from 'Services/dataApi';
import pdfMake from 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';
import { openPdfInNewTabOrAlert } from 'Services/pdfOpen';
import TemplateCanvas from './TemplateCanvas';
import {
  createBindingData,
  evaluatorSchedulePreviewRecords,
  getBindingGroups,
  getBindingLabel,
  getTableBindingFields,
  getTableBindings,
} from './TemplateBindings';
import {
  createTemplateRegions,
  getCanvasMargins,
  getDataContentBounds,
  getRegionElements,
  getRegionHeight,
  getRepeatCardLayout,
  normalizeTemplate,
} from './TemplateLayout';
import { buildTemplatePdfDefinition } from './TemplatePdfRenderer';
import './PdfTemplateDesigner.css';

const createTemplate = () => {
  return {
    schemaVersion: 2,
    name: 'Untitled PDF Template',
    description: '',
    canvas: {
      width: 612,
      height: 792,
      margins: { top: 0, right: 0, bottom: 0, left: 0 },
      unit: 'pt',
    },
    regions: createTemplateRegions(),
  };
};

const createElement = (type, index) => {
  const isText = type === 'text';
  const isImage = type === 'image';
  const isLine = type === 'line';
  const isRectangle = type === 'rectangle';
  const isTable = type === 'table';
  const columns = isTable
    ? [
        { key: 'column1', label: 'Column 1', labelAuto: true, width: 100 },
        { key: 'column2', label: 'Column 2', labelAuto: true, width: 100 },
      ]
    : undefined;

  return {
    id: `${type}-${Date.now()}-${index}`,
    type,
    x: 72,
    y: 72 + index * 42,
    width: isText ? 220 : isImage ? 180 : isTable ? 320 : 160,
    height: isText ? 28 : isImage ? 135 : isTable ? 140 : isLine ? 12 : 48,
    content: isText ? 'Text block' : '',
    orientation: isLine ? 'horizontal' : undefined,
    imageUrl: isImage ? '' : undefined,
    imageMode: isImage ? 'contain' : undefined,
    name: isImage ? 'Uploaded Image' : undefined,
    columns,
    tableData: isTable
      ? [
          { column1: 'Row 1', column2: '' },
          { column1: 'Row 2', column2: '' },
          { column1: 'Row 3', column2: '' },
        ]
      : undefined,
    showHeaders: isTable ? true : undefined,
    alternateRowColors: isTable ? true : undefined,
    styles: isText
      ? { fontSize: 14, fontWeight: 'normal', color: '#1f2937', textAlign: 'left' }
      : isImage
        ? { fill: '#f8fafc', stroke: '#94a3b8', strokeWidth: 1 }
        : isLine
          ? { stroke: '#2b6cb0', strokeWidth: 1 }
        : isRectangle
          ? { fill: 'transparent', stroke: '#2b6cb0', strokeWidth: 1 }
          : { fill: '#e8f1fb', stroke: '#2b6cb0', strokeWidth: 1 },
  };
};

const createRegionElement = (type, index, regionName, template) => {
  if (regionName === 'dataContent' && template.regions.dataContent.mode === 'repeat') {
    const cardLayout = getRepeatCardLayout(template);

    if (cardLayout.cardHeight < 24 || cardLayout.cardWidth < 24) {
      return null;
    }

    const element = createElement(type, index);
    const height = Math.min(element.height, cardLayout.cardHeight);
    const width = Math.min(element.width, cardLayout.cardWidth);

    return {
      ...element,
      height,
      width,
      x: Math.max(0, Math.min(cardLayout.cardWidth - width, 12 + index * 42)),
      y: Math.max(0, Math.min(cardLayout.cardHeight - height, 12 + index * 42)),
    };
  }

  const headerHeight = template.regions.header.height;
  const footerHeight = template.regions.footer.height;
  const regionStartY =
    regionName === 'header'
      ? 0
      : regionName === 'footer'
        ? template.canvas.height - footerHeight
        : headerHeight;
  const regionEndY = regionName === 'header' ? headerHeight : regionName === 'footer' ? template.canvas.height : template.canvas.height - footerHeight;
  const regionHeight = regionEndY - regionStartY;

  if (regionHeight < 24) {
    return null;
  }

  const element = createElement(type, index);
  const height = Math.min(element.height, regionHeight);

  return {
    ...element,
    height,
    y: Math.max(regionStartY, Math.min(regionEndY - height, regionStartY + 12 + index * 42)),
  };
};

const PdfTemplateDesigner = ({ candidates = [], updateChanges }) => {
  const [template, setTemplate] = useState(createTemplate);
  const [templates, setTemplates] = useState([]);
  const [rawTemplate, setRawTemplate] = useState(() => {
    return JSON.stringify(createTemplate(), null, 2);
  });
  const [isEditingJSON, setIsEditingJSON] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedPreviewCandidateId, setSelectedPreviewCandidateId] = useState('');
  const [activeRegion, setActiveRegion] = useState('dataContent');
  const [templateBaseline, setTemplateBaseline] = useState(() => {
    return JSON.stringify(createTemplate(), null, 2);
  });
  const imageInputRef = useRef(null);
  const textContentInputRef = useRef(null);
  const reportedUnsavedChangesRef = useRef();
  const dContext = useContext(DepartmentContext);
  const { department, season } = dContext || {};
  const { EnableSuperUser } = department || {};
  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const departmentId = department?.pk_Department;

  const templateJSON = useMemo(() => {
    return JSON.stringify(template, null, 2);
  }, [template]);
  const hasUnsavedChanges = isNewTemplate || templateJSON !== templateBaseline || (isEditingJSON && rawTemplate !== templateJSON);

  const previewCandidates = useMemo(() => {
    const uniqueCandidates = Object.values(
      candidates.reduce((candidatesById, candidate) => {
        if (candidate.pk_Candidate != null) {
          candidatesById[candidate.pk_Candidate] = candidate;
        }

        return candidatesById;
      }, {}),
    );

    return uniqueCandidates.sort((firstCandidate, secondCandidate) => {
      const lastNameComparison = (firstCandidate.LastName || '').localeCompare(secondCandidate.LastName || '');

      if (lastNameComparison !== 0) {
        return lastNameComparison;
      }

      return (firstCandidate.FirstName || '').localeCompare(secondCandidate.FirstName || '');
    });
  }, [candidates]);

  const selectedPreviewCandidate = useMemo(() => {
    return previewCandidates.find((candidate) => {
      return String(candidate.pk_Candidate) === String(selectedPreviewCandidateId);
    });
  }, [previewCandidates, selectedPreviewCandidateId]);

  const cardPreviewCandidates = useMemo(() => {
    if (!selectedPreviewCandidate) {
      return previewCandidates;
    }

    return [
      selectedPreviewCandidate,
      ...previewCandidates.filter((candidate) => {
        return candidate.pk_Candidate !== selectedPreviewCandidate.pk_Candidate;
      }),
    ];
  }, [previewCandidates, selectedPreviewCandidate]);

  const dataSource = template.regions.dataContent.dataSource;

  const bindingData = useMemo(() => {
    return createBindingData({
      candidate: selectedPreviewCandidate,
      department,
      evaluator: dataSource === 'evaluators' ? evaluatorSchedulePreviewRecords[0] : null,
      season,
    });
  }, [dataSource, department, season, selectedPreviewCandidate]);

  const cardPreviewRecords = useMemo(() => {
    if (dataSource === 'evaluators') {
      return evaluatorSchedulePreviewRecords.map((evaluatorSchedule) => {
        return createBindingData({ department, evaluator: evaluatorSchedule, season });
      });
    }

    return cardPreviewCandidates.map((candidate) => {
      return createBindingData({ candidate, department, season });
    });
  }, [cardPreviewCandidates, dataSource, department, season]);

  const bindingGroupsForActiveRegion = useMemo(() => {
    return getBindingGroups(dataSource, activeRegion);
  }, [activeRegion, dataSource]);

  const updateTemplate = useCallback((updatedTemplate) => {
    const normalizedTemplate = normalizeTemplate(updatedTemplate);

    setTemplate(normalizedTemplate);
    setRawTemplate(JSON.stringify(normalizedTemplate, null, 2));
  }, []);

  const setTemplateBaselineFrom = (templateValue) => {
    setTemplateBaseline(JSON.stringify(templateValue, null, 2));
  };

  const getTemplatePayload = () => {
    return {
      Name: (template.name || 'Untitled PDF Template').trim(),
      Description: template.description || '',
      SchemaVersion: Number.isInteger(template.schemaVersion) ? template.schemaVersion : 1,
      TemplateJson: JSON.stringify(template),
    };
  };

  const loadTemplateRecord = (templateRecord) => {
    try {
      const parsedTemplate = JSON.parse(templateRecord.TemplateJson);
      const loadedTemplate = normalizeTemplate({
        ...parsedTemplate,
        name: templateRecord.Name,
        description: templateRecord.Description || '',
        schemaVersion: templateRecord.SchemaVersion,
      });

      updateTemplate(loadedTemplate);
      setTemplateBaselineFrom(loadedTemplate);
      setSelectedTemplateId(templateRecord.pk_PDFTemplate);
      setIsNewTemplate(false);
      setSelectedElementId(null);
      setActiveRegion('dataContent');
      setIsEditingJSON(false);
    } catch (error) {
      toast.error(`Unable to load ${templateRecord.Name}`);
    }
  };

  const loadTemplates = useCallback(() => {
    if (!departmentId) {
      setTemplates([]);
      setSelectedTemplateId(null);
      return;
    }

    setIsLoadingTemplates(true);
    getTokenSilently()
      .then((token) => {
        return fetchDataAgnostic(
          'department/pdfTemplates',
          { pk_Department: departmentId },
          formatBearerToken(token),
        );
      })
      .then((result) => {
        setTemplates(result.data || []);
      })
      .catch((error) => {
        checkLoginForError({ err: error, loginWithRedirect });
        toast.error('Failed to load PDF templates');
      })
      .finally(() => {
        setIsLoadingTemplates(false);
      });
  }, [departmentId, getTokenSilently, loginWithRedirect]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (reportedUnsavedChangesRef.current === hasUnsavedChanges) {
      return;
    }

    reportedUnsavedChangesRef.current = hasUnsavedChanges;
    updateChanges?.('pdfTemplate', hasUnsavedChanges);
  }, [hasUnsavedChanges, updateChanges]);

  const updateSavedTemplate = (templateRecord) => {
    setTemplates((currentTemplates) => {
      const withoutUpdatedTemplate = currentTemplates.filter((item) => {
        return item.pk_PDFTemplate !== templateRecord.pk_PDFTemplate;
      });

      return [...withoutUpdatedTemplate, templateRecord].sort((firstTemplate, secondTemplate) => {
        return firstTemplate.Name.localeCompare(secondTemplate.Name);
      });
    });
  };

  const handleCreateTemplate = (markAsNew = true) => {
    const newTemplate = createTemplate();
    updateTemplate(newTemplate);
    setTemplateBaselineFrom(newTemplate);
    setIsNewTemplate(markAsNew);
    setIsEditingJSON(false);
    setSelectedElementId(null);
    setSelectedTemplateId(null);
    setActiveRegion('dataContent');
    toast.success('New unsaved PDF template created');
  };

  const handleTemplateSelection = (event) => {
    const templateId = Number(event.target.value);

    if (!templateId) {
      handleCreateTemplate();
      return;
    }

    const templateRecord = templates.find((item) => {
      return item.pk_PDFTemplate === templateId;
    });

    if (templateRecord) {
      loadTemplateRecord(templateRecord);
    }
  };

  const handleSaveTemplate = () => {
    if (!departmentId) {
      toast.error('Select a department before saving a PDF template');
      return;
    }

    const templatePayload = getTemplatePayload();

    if (!templatePayload.Name) {
      toast.error('A template name is required');
      return;
    }

    setIsSavingTemplate(true);
    getTokenSilently()
      .then((token) => {
        const headers = formatBearerToken(token);

        if (selectedTemplateId) {
          return putData(
            'department/pdfTemplates',
            { pk_Department: departmentId, pk_PDFTemplate: selectedTemplateId },
            { template: templatePayload },
            headers,
          );
        }

        return postDataAgnostic(
          'department/pdfTemplates',
          { pk_Department: departmentId },
          { template: templatePayload },
          headers,
        );
      })
      .then((result) => {
        const savedTemplate = result.data;
        updateSavedTemplate(savedTemplate);
        setSelectedTemplateId(savedTemplate.pk_PDFTemplate);
        setTemplateBaseline(templateJSON);
        setIsNewTemplate(false);
        toast.success('PDF template saved');
      })
      .catch((error) => {
        checkLoginForError({ err: error, loginWithRedirect });
        toast.error('Failed to save PDF template');
      })
      .finally(() => {
        setIsSavingTemplate(false);
      });
  };

  const handleDeleteTemplate = () => {
    if (!selectedTemplateId || !departmentId) {
      return;
    }

    if (!window.confirm('Delete this PDF template?')) {
      return;
    }

    setIsSavingTemplate(true);
    getTokenSilently()
      .then((token) => {
        return deleteData(
          'department/pdfTemplates',
          { pk_Department: departmentId, pk_PDFTemplate: selectedTemplateId },
          {},
          formatBearerToken(token),
        );
      })
      .then(() => {
        setTemplates((currentTemplates) => {
          return currentTemplates.filter((item) => {
            return item.pk_PDFTemplate !== selectedTemplateId;
          });
        });
        handleCreateTemplate(false);
        toast.success('PDF template deleted');
      })
      .catch((error) => {
        checkLoginForError({ err: error, loginWithRedirect });
        toast.error('Failed to delete PDF template');
      })
      .finally(() => {
        setIsSavingTemplate(false);
      });
  };

  const handleAddElement = (type) => {
    const regionElements = getRegionElements(template, activeRegion);
    const newElement = createRegionElement(type, regionElements.length, activeRegion, template);

    if (!newElement) {
      toast.error('Increase this region height before adding an element');
      return;
    }
    const updatedTemplate = {
      ...template,
      regions: {
        ...template.regions,
        [activeRegion]: {
          ...template.regions[activeRegion],
          elements: [...regionElements, newElement],
        },
      },
    };

    updateTemplate(updatedTemplate);
    setSelectedElementId(newElement.id);
  };

  const addImageElement = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }

    const regionElements = getRegionElements(template, activeRegion);
    const imageElement = createRegionElement('image', regionElements.length, activeRegion, template);

    if (!imageElement) {
      toast.error('Increase this region height before adding an element');
      return;
    }

    const imageReader = new FileReader();

    imageReader.onload = () => {
      const newElement = {
        ...imageElement,
        imageUrl: imageReader.result,
        name: file.name,
      };
      const updatedTemplate = {
        ...template,
        regions: {
          ...template.regions,
          [activeRegion]: {
            ...template.regions[activeRegion],
            elements: [...regionElements, newElement],
          },
        },
      };

      updateTemplate(updatedTemplate);
      setSelectedElementId(newElement.id);
    };
    imageReader.onerror = () => {
      toast.error('The image could not be read');
    };
    imageReader.readAsDataURL(file);
  };

  const handleImageUpload = (event) => {
    const [file] = event.target.files || [];

    addImageElement(file);
    event.target.value = '';
  };

  const handleImageReplacement = (event, elementId) => {
    const [file] = event.target.files || [];

    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }

    const imageReader = new FileReader();

    imageReader.onload = () => {
      handleElementUpdate(elementId, { imageUrl: imageReader.result, name: file.name });
    };
    imageReader.onerror = () => {
      toast.error('The image could not be read');
    };
    imageReader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleTableColumnCountChange = (element, columnCount) => {
    const safeColumnCount = Math.max(1, Math.min(8, Number(columnCount) || 1));
    const columns = Array.from({ length: safeColumnCount }).map((item, index) => {
      const existingColumn = element.columns?.[index];

      return existingColumn || {
        key: `column${index + 1}`,
        label: `Column ${index + 1}`,
        labelAuto: true,
        width: 100,
      };
    });
    const tableData = (element.tableData || []).map((row) => {
      return columns.reduce((newRow, column) => {
        newRow[column.key] = row[column.key] || '';
        return newRow;
      }, {});
    });

    handleElementUpdate(element.id, { columns, tableData });
  };

  const handleTableRowCountChange = (element, rowCount) => {
    const safeRowCount = Math.max(1, Math.min(20, Number(rowCount) || 1));
    const currentRows = element.tableData || [];
    const columns = element.columns || [];
    const tableData = Array.from({ length: safeRowCount }).map((item, index) => {
      if (currentRows[index]) {
        return currentRows[index];
      }

      return columns.reduce((newRow, column) => {
        newRow[column.key] = '';
        return newRow;
      }, {});
    });

    handleElementUpdate(element.id, { tableData });
  };

  const handleTableColumnLabelChange = (element, columnKey, label) => {
    const columns = (element.columns || []).map((column) => {
      if (column.key !== columnKey) {
        return column;
      }

      return { ...column, label, labelAuto: false };
    });

    handleElementUpdate(element.id, { columns });
  };

  const handleTableColumnBindingChange = (element, columnKey, fieldPath) => {
    const columns = (element.columns || []).map((column) => {
      if (column.key !== columnKey) {
        return column;
      }

      const field = getTableBindingFields(dataSource, element.fieldPath).find((item) => {
        return item.bindingPath === fieldPath;
      });

      return {
        ...column,
        fieldPath: fieldPath || undefined,
        label: column.labelAuto && field ? field.label : column.label,
      };
    });

    handleElementUpdate(element.id, { columns });
  };

  const handleTableCellChange = (element, rowIndex, columnKey, value) => {
    const tableData = (element.tableData || []).map((row, index) => {
      if (index !== rowIndex) {
        return row;
      }

      return { ...row, [columnKey]: value };
    });

    handleElementUpdate(element.id, { tableData });
  };

  const handleElementChange = (elementId, key, value) => {
    const parsedValue = key === 'content' ? value : Number(value);

    handleElementUpdate(elementId, { [key]: parsedValue });
  };

  const handleElementUpdate = (elementId, updates, regionName = activeRegion) => {
    const updatedTemplate = {
      ...template,
      regions: {
        ...template.regions,
        [regionName]: {
          ...template.regions[regionName],
          elements: getRegionElements(template, regionName).map((element) => {
            if (element.id !== elementId) {
              return element;
            }

            return {
              ...element,
              ...updates,
            };
          }),
        },
      },
    };

    updateTemplate(updatedTemplate);
  };

  const handleBindingChange = (elementId, fieldPath) => {
    handleElementUpdate(elementId, {
      fieldPath: fieldPath || undefined,
      displayName: fieldPath ? getBindingLabel(fieldPath) : undefined,
    });
  };

  const handleTextFontSizeChange = (element, fontSize) => {
    const safeFontSize = Math.max(6, Math.min(72, Number(fontSize) || 6));

    handleElementUpdate(element.id, {
      styles: {
        ...element.styles,
        fontSize: safeFontSize,
      },
    });
  };

  const handleTextAlignmentChange = (element, textAlign) => {
    handleElementUpdate(element.id, {
      styles: {
        ...element.styles,
        textAlign,
      },
    });
  };

  const handleRectangleFillChange = (element, fill) => {
    handleElementUpdate(element.id, {
      styles: {
        ...element.styles,
        fill,
      },
    });
  };

  const handleRectangleOutlineChange = (element, stroke) => {
    handleElementUpdate(element.id, {
      styles: {
        ...element.styles,
        stroke,
      },
    });
  };

  const handleLineOrientationChange = (element, orientation) => {
    const isHorizontal = orientation === 'horizontal';
    const currentLength = element.orientation === 'vertical' ? element.height : element.width;

    handleElementUpdate(element.id, {
      height: isHorizontal ? 12 : currentLength,
      orientation,
      width: isHorizontal ? currentLength : 12,
    });
  };

  const handleLineStyleChange = (element, key, value) => {
    const parsedValue = key === 'strokeWidth' ? Math.max(1, Math.min(12, Number(value) || 1)) : value;

    handleElementUpdate(element.id, {
      styles: {
        ...element.styles,
        [key]: parsedValue,
      },
    });
  };

  const handleRegionHeightChange = (regionName, value) => {
    const otherRegionName = regionName === 'header' ? 'footer' : 'header';
    const margins = getCanvasMargins(template);
    const otherRegionHeight = getRegionHeight(template, otherRegionName);
    const maximumHeight = Math.max(0, template.canvas.height - margins.top - margins.bottom - otherRegionHeight - 100);
    const height = Math.max(0, Math.min(maximumHeight, Number(value) || 0));

    updateTemplate({
      ...template,
      regions: {
        ...template.regions,
        [regionName]: {
          ...template.regions[regionName],
          height,
        },
      },
    });
  };

  const handleRegionVisibilityChange = (regionName, enabled) => {
    updateTemplate({
      ...template,
      regions: {
        ...template.regions,
        [regionName]: {
          ...template.regions[regionName],
          enabled,
        },
      },
    });

    if (!enabled && activeRegion === regionName) {
      setActiveRegion('dataContent');
      setSelectedElementId(null);
    }
  };

  const handleCanvasMarginChange = (marginName, value) => {
    const margins = getCanvasMargins(template);
    const isHorizontalMargin = marginName === 'left' || marginName === 'right';
    const oppositeMarginName = marginName === 'left' ? 'right' : marginName === 'right' ? 'left' : marginName === 'top' ? 'bottom' : 'top';
    const canvasDimension = isHorizontalMargin ? template.canvas.width : template.canvas.height;
    const maximumMargin = Math.max(0, canvasDimension - margins[oppositeMarginName] - 100);
    const marginValue = Math.max(0, Math.min(maximumMargin, Number(value) || 0));

    updateTemplate({
      ...template,
      canvas: {
        ...template.canvas,
        margins: {
          ...margins,
          [marginName]: marginValue,
        },
      },
    });
  };

  const handleDataContentChange = (updates) => {
    updateTemplate({
      ...template,
      regions: {
        ...template.regions,
        dataContent: {
          ...template.regions.dataContent,
          ...updates,
        },
      },
    });
  };

  const handleDataContentModeChange = (mode) => {
    const { dataContent } = template.regions;

    if (dataContent.mode === mode) {
      return;
    }

    const dataContentBounds = getDataContentBounds(template);
    const cardLayout = getRepeatCardLayout(template);
    const elements = getRegionElements(template, 'dataContent').map((element) => {
      if (mode === 'repeat') {
        const width = Math.min(element.width, cardLayout.cardWidth);
        const height = Math.min(element.height, cardLayout.cardHeight);

        return {
          ...element,
          height,
          width,
          x: Math.max(0, Math.min(cardLayout.cardWidth - width, element.x)),
          y: Math.max(0, Math.min(cardLayout.cardHeight - height, element.y - dataContentBounds.y)),
        };
      }

      return {
        ...element,
        x: Math.max(0, element.x),
        y: Math.max(0, element.y + dataContentBounds.y),
      };
    });

    handleDataContentChange({ elements, mode });
  };

  const handleRepeatLayoutChange = (key, value) => {
    handleDataContentChange({
      repeatLayout: {
        ...template.regions.dataContent.repeatLayout,
        [key]: value,
      },
    });
  };

  const handleRemoveElement = useCallback((elementId) => {
    const updatedTemplate = {
      ...template,
      regions: {
        ...template.regions,
        [activeRegion]: {
          ...template.regions[activeRegion],
          elements: getRegionElements(template, activeRegion).filter((element) => {
            return element.id !== elementId;
          }),
        },
      },
    };

    updateTemplate(updatedTemplate);
    setSelectedElementId((currentSelectedElementId) => {
      return currentSelectedElementId === elementId ? null : currentSelectedElementId;
    });
  }, [activeRegion, template, updateTemplate]);

  const handleApplyJSON = () => {
    try {
      const parsedTemplate = JSON.parse(rawTemplate);

      if (!parsedTemplate || Array.isArray(parsedTemplate) || typeof parsedTemplate !== 'object') {
        throw new Error('Template must be a JSON object');
      }

      if (!Array.isArray(parsedTemplate.elements) && !Array.isArray(parsedTemplate.regions?.dataContent?.elements)) {
        throw new Error('Template must include data-content elements');
      }

      if (
        !parsedTemplate.canvas ||
        !Number.isFinite(parsedTemplate.canvas.width) ||
        !Number.isFinite(parsedTemplate.canvas.height)
      ) {
        throw new Error('Template must include numeric canvas width and height values');
      }

      updateTemplate(parsedTemplate);
      setIsEditingJSON(false);
      setSelectedElementId(null);
      toast.success('Template JSON applied');
    } catch (err) {
      toast.error(err.message || 'Template JSON is invalid');
    }
  };

  const handleExportTemplate = () => {
    const exportBlob = new Blob([templateJSON], { type: 'application/json' });
    const exportURL = URL.createObjectURL(exportBlob);
    const exportLink = document.createElement('a');
    const fileName = (template.name || 'pdf-template').trim().replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();

    exportLink.href = exportURL;
    exportLink.download = `${fileName || 'pdf-template'}.json`;
    document.body.appendChild(exportLink);
    exportLink.click();
    document.body.removeChild(exportLink);
    URL.revokeObjectURL(exportURL);
  };

  const handlePreviewPdf = () => {
    const previewRecords = dataSource ? cardPreviewRecords : [bindingData];

    if (template.regions.dataContent.mode === 'repeat' && previewRecords.length === 0) {
      toast.error('Select a card data source with at least one record before previewing');
      return;
    }

    try {
      const docDefinition = buildTemplatePdfDefinition({
        bindingData,
        records: previewRecords,
        template,
      });
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      const downloadTitle = (template.name || 'RezRATE_Print_Materials').trim().replace(/[^a-z0-9-_]+/gi, '_');

      openPdfInNewTabOrAlert({
        downloadTitle,
        pdfDocGenerator,
      });
    } catch (error) {
      toast.error('Unable to generate the PDF preview');
    }
  };

  const selectedElement = getRegionElements(template, activeRegion).find((element) => {
    return element.id === selectedElementId;
  });

  useEffect(() => {
    if (selectedElement?.type !== 'text' || selectedElement.fieldPath) {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      textContentInputRef.current?.focus();
      textContentInputRef.current?.select();
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [selectedElement?.fieldPath, selectedElement?.type, selectedElementId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tagName = event.target?.tagName;
      const isEditingField =
        event.target?.isContentEditable || tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA';
      const isSelectedTextContent =
        event.target === textContentInputRef.current &&
        event.target.selectionStart === 0 &&
        event.target.selectionEnd === event.target.value.length;
      const isDeleteKey = event.key === 'Backspace' || event.key === 'Delete';

      if (!isDeleteKey || !selectedElementId || (isEditingField && !isSelectedTextContent)) {
        return;
      }

      event.preventDefault();
      handleRemoveElement(selectedElementId);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleRemoveElement, selectedElementId]);

  return (
    <div className="pdf-template-designer">
      <Card className="pdf-template-designer__card">
        <CardHeader className="pdf-template-designer__header">
          <div>
            <h4>PDF Template Designer</h4>
            <div className="pdf-template-designer__subtitle">
              Build and arrange template elements visually. PDF rendering is planned next.
            </div>
          </div>
          <div className="pdf-template-designer__header-actions">
            <Button color="secondary" disabled={isSavingTemplate} outline onClick={handleCreateTemplate}>
              New Template
            </Button>
            <Button color="success" disabled={!hasUnsavedChanges || isSavingTemplate || isLoadingTemplates} onClick={handleSaveTemplate}>
              <i className="fa fa-save" /> {isSavingTemplate ? 'Saving...' : 'Save Template'}
            </Button>
            <Button color="danger" disabled={!selectedTemplateId || isSavingTemplate} outline onClick={handleDeleteTemplate}>
              <i className="fa fa-trash" /> Delete
            </Button>
            <Button color="primary" onClick={handlePreviewPdf}>
              <i className="fa fa-file-pdf-o" /> Preview PDF
            </Button>
            {EnableSuperUser ? (
              <Button color="primary" onClick={handleExportTemplate}>
                <i className="fa fa-download" /> Export Template JSON
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardBody>
          <div className="pdf-template-designer__layout">
            <section className="pdf-template-designer__panel">
              <h5>Template Details</h5>
              <FormGroup>
                <Label for="pdf-template-list">Saved Templates</Label>
                <Input
                  disabled={isLoadingTemplates || isSavingTemplate}
                  id="pdf-template-list"
                  onChange={handleTemplateSelection}
                  type="select"
                  value={selectedTemplateId || ''}
                >
                  <option value="">New unsaved template</option>
                  {templates.map((templateRecord) => {
                    return (
                      <option key={templateRecord.pk_PDFTemplate} value={templateRecord.pk_PDFTemplate}>
                        {templateRecord.Name}
                      </option>
                    );
                  })}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label for="pdf-template-name">Template Name</Label>
                <Input
                  id="pdf-template-name"
                  value={template.name || ''}
                  onChange={(event) => {
                    updateTemplate({ ...template, name: event.target.value });
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label for="pdf-template-description">Description</Label>
                <Input
                  id="pdf-template-description"
                  type="textarea"
                  rows="3"
                  value={template.description || ''}
                  onChange={(event) => {
                    updateTemplate({ ...template, description: event.target.value });
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label for="pdf-template-preview-candidate">Preview Candidate</Label>
                <Input
                  id="pdf-template-preview-candidate"
                  onChange={(event) => { setSelectedPreviewCandidateId(event.target.value); }}
                  type="select"
                  value={selectedPreviewCandidateId}
                >
                  <option value="">No candidate selected</option>
                  {previewCandidates.map((candidate) => {
                    const candidateName = [candidate.LastName, candidate.FirstName].filter(Boolean).join(', ');

                    return (
                      <option key={candidate.pk_Candidate} value={candidate.pk_Candidate}>
                        {candidateName || `Candidate ${candidate.pk_Candidate}`}
                      </option>
                    );
                  })}
                </Input>
                <small className="form-text text-muted">Select a candidate to preview candidate fields with live data.</small>
              </FormGroup>
              <div className="pdf-template-designer__canvas-details">
                <span>Canvas: {template.canvas?.width || 612} x {template.canvas?.height || 792} {template.canvas?.unit || 'pt'}</span>
                <span>{getRegionElements(template, activeRegion).length} element{getRegionElements(template, activeRegion).length === 1 ? '' : 's'}</span>
              </div>
              <div className="pdf-template-designer__page-margins">
                <Label>
                  Top Margin
                  <Input
                    min="0"
                    onChange={(event) => { handleCanvasMarginChange('top', event.target.value); }}
                    type="number"
                    value={getCanvasMargins(template).top}
                  />
                </Label>
                <Label>
                  Right Margin
                  <Input
                    min="0"
                    onChange={(event) => { handleCanvasMarginChange('right', event.target.value); }}
                    type="number"
                    value={getCanvasMargins(template).right}
                  />
                </Label>
                <Label>
                  Bottom Margin
                  <Input
                    min="0"
                    onChange={(event) => { handleCanvasMarginChange('bottom', event.target.value); }}
                    type="number"
                    value={getCanvasMargins(template).bottom}
                  />
                </Label>
                <Label>
                  Left Margin
                  <Input
                    min="0"
                    onChange={(event) => { handleCanvasMarginChange('left', event.target.value); }}
                    type="number"
                    value={getCanvasMargins(template).left}
                  />
                </Label>
              </div>
              <div className="pdf-template-designer__region-toggles">
                <div className="form-check form-switch">
                  <Input
                    checked={template.regions.header.enabled !== false}
                    className="form-check-input"
                    id="pdf-template-use-header"
                    onChange={(event) => { handleRegionVisibilityChange('header', event.target.checked); }}
                    role="switch"
                    type="checkbox"
                  />
                  <Label className="form-check-label" for="pdf-template-use-header">
                    Use Header
                  </Label>
                </div>
                <div className="form-check form-switch">
                  <Input
                    checked={template.regions.footer.enabled !== false}
                    className="form-check-input"
                    id="pdf-template-use-footer"
                    onChange={(event) => { handleRegionVisibilityChange('footer', event.target.checked); }}
                    role="switch"
                    type="checkbox"
                  />
                  <Label className="form-check-label" for="pdf-template-use-footer">
                    Use Footer
                  </Label>
                </div>
              </div>
              <FormGroup>
                <Label>Layout Region</Label>
                <div className="pdf-template-designer__region-actions">
                  {[
                    { key: 'header', label: 'Header' },
                    { key: 'dataContent', label: 'Data Content' },
                    { key: 'footer', label: 'Footer' },
                  ].map((region) => {
                    return (
                      <Button
                        color="primary"
                        disabled={region.key !== 'dataContent' && template.regions[region.key].enabled === false}
                        key={region.key}
                        onClick={() => {
                          setActiveRegion(region.key);
                          setSelectedElementId(null);
                        }}
                        outline={activeRegion !== region.key}
                        size="sm"
                      >
                        {region.label}
                      </Button>
                    );
                  })}
                </div>
              </FormGroup>
              <div className="pdf-template-designer__region-heights">
                <Label>
                  Header Height
                  <Input
                    disabled={template.regions.header.enabled === false}
                    min="0"
                    onChange={(event) => { handleRegionHeightChange('header', event.target.value); }}
                    type="number"
                    value={template.regions.header.height}
                  />
                </Label>
                <Label>
                  Footer Height
                  <Input
                    disabled={template.regions.footer.enabled === false}
                    min="0"
                    onChange={(event) => { handleRegionHeightChange('footer', event.target.value); }}
                    type="number"
                    value={template.regions.footer.height}
                  />
                </Label>
              </div>
              {activeRegion === 'dataContent' ? (
                <div className="pdf-template-designer__data-content-settings">
                  <h6>Data Content Settings</h6>
                  <FormGroup>
                    <Label for="pdf-template-data-content-mode">Content Mode</Label>
                    <Input
                      id="pdf-template-data-content-mode"
                      onChange={(event) => { handleDataContentModeChange(event.target.value); }}
                      type="select"
                      value={template.regions.dataContent.mode}
                    >
                      <option value="single">Single Content</option>
                      <option value="repeat">Repeat Cards</option>
                    </Input>
                  </FormGroup>
                  {template.regions.dataContent.mode === 'repeat' ? (
                    <div className="pdf-template-designer__repeat-settings">
                      <Label>
                        Cards Per Row
                        <Input
                          max="4"
                          min="1"
                          onChange={(event) => { handleRepeatLayoutChange('columns', Math.max(1, Math.min(4, Number(event.target.value) || 1))); }}
                          type="number"
                          value={template.regions.dataContent.repeatLayout.columns}
                        />
                      </Label>
                      <Label>
                        Rows Per Page
                        <Input
                          max="6"
                          min="1"
                          onChange={(event) => { handleRepeatLayoutChange('rows', Math.max(1, Math.min(6, Number(event.target.value) || 1))); }}
                          type="number"
                          value={template.regions.dataContent.repeatLayout.rows}
                        />
                      </Label>
                      <Label>
                        Card Gap
                        <Input
                          min="0"
                          onChange={(event) => { handleRepeatLayoutChange('gap', Math.max(0, Number(event.target.value) || 0)); }}
                          type="number"
                          value={template.regions.dataContent.repeatLayout.gap}
                        />
                      </Label>
                      <Label>
                        Page Break
                        <Input
                          onChange={(event) => { handleRepeatLayoutChange('pageBreak', event.target.value); }}
                          type="select"
                          value={template.regions.dataContent.repeatLayout.pageBreak}
                        >
                          <option value="auto">Automatic</option>
                          <option value="afterEach">After Each Card</option>
                        </Input>
                      </Label>
                      <Label className="pdf-template-designer__checkbox-label">
                        <Input
                          checked={template.regions.dataContent.repeatLayout.keepTogether}
                          onChange={(event) => { handleRepeatLayoutChange('keepTogether', event.target.checked); }}
                          type="checkbox"
                        />
                        Keep card together
                      </Label>
                    </div>
                  ) : null}
                  <small className="form-text text-muted">
                    {template.regions.dataContent.mode === 'repeat'
                      ? 'The canvas edits one card. PDF rendering will repeat it for the selected data source.'
                      : 'Single Content fills the available space between the Header and Footer.'}
                  </small>
                </div>
              ) : null}
              <div className="pdf-template-designer__add-actions">
                <Button color="primary" outline size="sm" onClick={() => { handleAddElement('text'); }}>
                  <i className="fa fa-font" /> Add Text
                </Button>
                <Button color="primary" outline size="sm" onClick={() => { handleAddElement('rectangle'); }}>
                  <i className="fa fa-square-o" /> Add Rectangle
                </Button>
                <Button color="primary" outline size="sm" onClick={() => { handleAddElement('line'); }}>
                  <i className="fa fa-minus" /> Add Line
                </Button>
                <Button color="primary" outline size="sm" onClick={() => { imageInputRef.current?.click(); }}>
                  <i className="fa fa-image" /> Add Image
                </Button>
                <Button color="primary" outline size="sm" onClick={() => { handleAddElement('table'); }}>
                  <i className="fa fa-table" /> Add Table
                </Button>
                <Input
                  accept="image/*"
                  aria-label="Upload an image element"
                  className="pdf-template-designer__hidden-file-input"
                  innerRef={imageInputRef}
                  onChange={handleImageUpload}
                  type="file"
                />
              </div>
            </section>

            <TemplateCanvas
              activeRegion={activeRegion}
              bindingData={bindingData}
              dataContentConfig={template.regions.dataContent}
              previewRecords={cardPreviewRecords}
              regionElements={{
                header: getRegionElements(template, 'header'),
                dataContent: getRegionElements(template, 'dataContent'),
                footer: getRegionElements(template, 'footer'),
              }}
              selectedElementId={selectedElementId}
              setActiveRegion={setActiveRegion}
              setSelectedElementId={setSelectedElementId}
              template={template}
              updateElement={handleElementUpdate}
            />

            <section className="pdf-template-designer__panel pdf-template-designer__elements-panel">
              <h5>Selected Element</h5>
              {!selectedElement ? (
                <div className="pdf-template-designer__empty-state">Select an element on the canvas to edit it.</div>
              ) : (
                <div className="pdf-template-designer__element">
                  <div className="pdf-template-designer__element-title">
                    <strong>
                      {selectedElement.type === 'text'
                        ? 'Text Block'
                        : selectedElement.type === 'image'
                          ? 'Image'
                          : selectedElement.type === 'table'
                            ? 'Table'
                          : 'Shape'}
                    </strong>
                    <Button color="link" size="sm" onClick={() => { handleRemoveElement(selectedElement.id); }}>
                      Remove
                    </Button>
                  </div>
                  {activeRegion === 'dataContent' ? (
                    <FormGroup>
                      <Label for="pdf-template-card-data-source">Card Data Source</Label>
                      <Input
                        id="pdf-template-card-data-source"
                        onChange={(event) => { handleDataContentChange({ dataSource: event.target.value }); }}
                        type="select"
                        value={dataSource}
                      >
                        <option value="">No record data</option>
                        <option value="candidates">Candidates</option>
                        <option value="evaluators">Evaluators</option>
                      </Input>
                      <small className="form-text text-muted">This source supplies every repeated card. Program fields are always available.</small>
                    </FormGroup>
                  ) : null}
                  {selectedElement.type === 'text' ? (
                    <>
                      <FormGroup>
                        <Label for="pdf-template-selected-binding">Data Binding</Label>
                        <Input
                          id="pdf-template-selected-binding"
                          onChange={(event) => { handleBindingChange(selectedElement.id, event.target.value); }}
                          type="select"
                          value={selectedElement.fieldPath || ''}
                        >
                          <option value="">Static text</option>
                          {bindingGroupsForActiveRegion.map((group) => {
                            return (
                              <optgroup key={group.label} label={group.label}>
                                {group.fields.map((field) => {
                                  return <option key={field.bindingPath} value={field.bindingPath}>{field.label}</option>;
                                })}
                              </optgroup>
                            );
                          })}
                        </Input>
                        {selectedElement.fieldPath ? (
                          <small className="form-text text-muted">{selectedElement.displayName || selectedElement.fieldPath}</small>
                        ) : null}
                      </FormGroup>
                      <FormGroup>
                        <Label for="pdf-template-selected-content">Text</Label>
                        <Input
                          disabled={Boolean(selectedElement.fieldPath)}
                          id="pdf-template-selected-content"
                          innerRef={textContentInputRef}
                          value={selectedElement.content || ''}
                          onChange={(event) => { handleElementChange(selectedElement.id, 'content', event.target.value); }}
                        />
                        {selectedElement.fieldPath ? <small className="form-text text-muted">Clear the binding to edit static text.</small> : null}
                      </FormGroup>
                      <FormGroup>
                        <Label for="pdf-template-text-font-size">Font Size</Label>
                        <Input
                          id="pdf-template-text-font-size"
                          max="72"
                          min="6"
                          onChange={(event) => { handleTextFontSizeChange(selectedElement, event.target.value); }}
                          type="number"
                          value={selectedElement.styles?.fontSize || 14}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label for="pdf-template-text-alignment">Justification</Label>
                        <Input
                          id="pdf-template-text-alignment"
                          onChange={(event) => { handleTextAlignmentChange(selectedElement, event.target.value); }}
                          type="select"
                          value={selectedElement.styles?.textAlign || 'left'}
                        >
                          <option value="left">Align Left</option>
                          <option value="center">Centered</option>
                          <option value="right">Align Right</option>
                        </Input>
                      </FormGroup>
                    </>
                  ) : null}
                  {selectedElement.type === 'image' ? (
                    <FormGroup>
                      <Label for="pdf-template-image-file">Image File</Label>
                      <Input
                        accept="image/*"
                        id="pdf-template-image-file"
                        onChange={(event) => { handleImageReplacement(event, selectedElement.id); }}
                        type="file"
                      />
                      <small className="form-text text-muted">{selectedElement.name || 'No image selected'}</small>
                    </FormGroup>
                  ) : null}
                  {selectedElement.type === 'rectangle' ? (
                    <>
                      <FormGroup>
                        <Label for="pdf-template-rectangle-fill">Fill Color</Label>
                        <div className="pdf-template-designer__rectangle-fill-controls">
                          <Input
                            id="pdf-template-rectangle-fill"
                            onChange={(event) => { handleRectangleFillChange(selectedElement, event.target.value); }}
                            type="color"
                            value={
                              selectedElement.styles?.fill && selectedElement.styles.fill !== 'transparent'
                                ? selectedElement.styles.fill
                                : '#ffffff'
                            }
                          />
                          <Button
                            color="secondary"
                            onClick={() => { handleRectangleFillChange(selectedElement, 'transparent'); }}
                            outline
                            size="sm"
                          >
                            Clear Fill
                          </Button>
                        </div>
                      </FormGroup>
                      <FormGroup>
                        <Label for="pdf-template-rectangle-outline">Outline Color</Label>
                        <Input
                          id="pdf-template-rectangle-outline"
                          onChange={(event) => { handleRectangleOutlineChange(selectedElement, event.target.value); }}
                          type="color"
                          value={selectedElement.styles?.stroke || '#2b6cb0'}
                        />
                      </FormGroup>
                    </>
                  ) : null}
                  {selectedElement.type === 'line' ? (
                    <>
                      <FormGroup>
                        <Label for="pdf-template-line-orientation">Orientation</Label>
                        <Input
                          id="pdf-template-line-orientation"
                          onChange={(event) => { handleLineOrientationChange(selectedElement, event.target.value); }}
                          type="select"
                          value={selectedElement.orientation || 'horizontal'}
                        >
                          <option value="horizontal">Horizontal</option>
                          <option value="vertical">Vertical</option>
                        </Input>
                      </FormGroup>
                      <FormGroup>
                        <Label for="pdf-template-line-color">Line Color</Label>
                        <Input
                          id="pdf-template-line-color"
                          onChange={(event) => { handleLineStyleChange(selectedElement, 'stroke', event.target.value); }}
                          type="color"
                          value={selectedElement.styles?.stroke || '#2b6cb0'}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label for="pdf-template-line-width">Line Width</Label>
                        <Input
                          id="pdf-template-line-width"
                          max="12"
                          min="1"
                          onChange={(event) => { handleLineStyleChange(selectedElement, 'strokeWidth', event.target.value); }}
                          type="number"
                          value={selectedElement.styles?.strokeWidth || 1}
                        />
                      </FormGroup>
                    </>
                  ) : null}
                  {selectedElement.type === 'table' ? (
                    <div className="pdf-template-designer__table-editor">
                      {activeRegion === 'dataContent' && dataSource === 'evaluators' ? (
                        <FormGroup>
                          <Label for="pdf-template-table-binding">Table Data Binding</Label>
                          <Input
                            id="pdf-template-table-binding"
                            onChange={(event) => { handleElementUpdate(selectedElement.id, { fieldPath: event.target.value || undefined }); }}
                            type="select"
                            value={selectedElement.fieldPath || ''}
                          >
                            <option value="">Manual rows</option>
                            {getTableBindings(dataSource).map((tableBinding) => {
                              return <option key={tableBinding.bindingPath} value={tableBinding.bindingPath}>{tableBinding.label}</option>;
                            })}
                          </Input>
                        </FormGroup>
                      ) : null}
                      <div className="pdf-template-designer__table-size-controls">
                        <Label>
                          Columns
                          <Input
                            max="8"
                            min="1"
                            onChange={(event) => { handleTableColumnCountChange(selectedElement, event.target.value); }}
                            type="number"
                            value={selectedElement.columns?.length || 1}
                          />
                        </Label>
                        <Label>
                          Rows
                          <Input
                            max="20"
                            min="1"
                            onChange={(event) => { handleTableRowCountChange(selectedElement, event.target.value); }}
                            type="number"
                            value={selectedElement.tableData?.length || 1}
                          />
                        </Label>
                      </div>
                      <div className="pdf-template-designer__table-fields">
                        {(selectedElement.columns || []).map((column) => {
                          return (
                            <div className="pdf-template-designer__table-column-editor" key={column.key}>
                              <Label>
                                Header
                                <Input
                                  value={column.label || ''}
                                  onChange={(event) => { handleTableColumnLabelChange(selectedElement, column.key, event.target.value); }}
                                />
                              </Label>
                              {getTableBindingFields(dataSource, selectedElement.fieldPath).length > 0 ? (
                                <Label>
                                  Field
                                  <Input
                                    onChange={(event) => { handleTableColumnBindingChange(selectedElement, column.key, event.target.value); }}
                                    type="select"
                                    value={column.fieldPath || ''}
                                  >
                                    <option value="">Manual cell value</option>
                                    {getTableBindingFields(dataSource, selectedElement.fieldPath).map((field) => {
                                      return <option key={field.bindingPath} value={field.bindingPath}>{field.label}</option>;
                                    })}
                                  </Input>
                                </Label>
                              ) : null}
                            </div>
                          );
                        })}
                        {getTableBindingFields(dataSource, selectedElement.fieldPath).length === 0 && (selectedElement.tableData || []).map((row, rowIndex) => {
                          return (selectedElement.columns || []).map((column) => {
                            return (
                              <Label key={`${rowIndex}-${column.key}`}>
                                Row {rowIndex + 1}
                                <Input
                                  value={row[column.key] || ''}
                                  onChange={(event) => { handleTableCellChange(selectedElement, rowIndex, column.key, event.target.value); }}
                                />
                              </Label>
                            );
                          });
                        })}
                      </div>
                    </div>
                  ) : null}
                  <div className="pdf-template-designer__element-coordinates">
                    <Label>
                      X
                      <Input type="number" value={selectedElement.x} onChange={(event) => { handleElementChange(selectedElement.id, 'x', event.target.value); }} />
                    </Label>
                    <Label>
                      Y
                      <Input type="number" value={selectedElement.y} onChange={(event) => { handleElementChange(selectedElement.id, 'y', event.target.value); }} />
                    </Label>
                    <Label>
                      Width
                      <Input type="number" value={selectedElement.width} onChange={(event) => { handleElementChange(selectedElement.id, 'width', event.target.value); }} />
                    </Label>
                    <Label>
                      Height
                      <Input type="number" value={selectedElement.height} onChange={(event) => { handleElementChange(selectedElement.id, 'height', event.target.value); }} />
                    </Label>
                  </div>
                </div>
              )}
            </section>
          </div>

          {EnableSuperUser ? (
            <section className="pdf-template-designer__json-panel">
              <div className="pdf-template-designer__json-heading">
                <div>
                  <h5>Template JSON</h5>
                  <span>This is the complete raw template payload that will later be stored through the API.</span>
                </div>
                <Button color="secondary" outline size="sm" onClick={() => { setIsEditingJSON(!isEditingJSON); }}>
                  {isEditingJSON ? 'Close JSON Editor' : 'Edit JSON'}
                </Button>
              </div>
              {isEditingJSON ? (
                <div>
                  <Input
                    className="pdf-template-designer__json-input"
                    type="textarea"
                    value={rawTemplate}
                    onChange={(event) => { setRawTemplate(event.target.value); }}
                  />
                  <div className="pdf-template-designer__json-actions">
                    <Button color="primary" size="sm" onClick={handleApplyJSON}>Apply JSON</Button>
                  </div>
                </div>
              ) : (
                <pre className="pdf-template-designer__json-preview">{templateJSON}</pre>
              )}
            </section>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
};

export default PdfTemplateDesigner;
